import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Status } from 'lavva.exalushome/build/js/DataFrame';
import { IUser } from 'lavva.exalushome/build/js/Services/Users/IUser';
import { User } from 'lavva.exalushome/build/js/Services/Users/User';
import { useBackdropContext } from '../../../../../context/backdrop';
import { toastSuccess } from '../../../../../utils/toast';
import { useExalusServicesContext } from '../../../context/services';
import { useHandleDataFrameErrors } from '../../../hooks/errors';
import { ChangePasswordForm, CreateEditUserForm } from '../types';

export const useUsers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('profile');
  const { userId } = useParams<{ userId: string }>();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { handleError } = useHandleDataFrameErrors();
  const { usersApi } = useExalusServicesContext();

  const changePassword = async (values: ChangePasswordForm) => {
    turnOnBackdrop();

    const result = await usersApi.ChangePasswordAsync({ Guid: userId } as IUser, values.Password);

    if (result.Type === Status.OK) {
      toastSuccess({ content: t('exalus.passwordChangedSuccessfully') });
    } else {
      handleError(result.Type, result.Type === Status.WrongData ? t(`exalus.${result.Data}`) : '');
    }

    turnOffBackdrop();
  };

  const deleteUser = async (id: string, onSuccess: Dispatch<SetStateAction<boolean>>) => {
    turnOnBackdrop();
    const result = await usersApi.DeleteUserAsync(id);

    if (result === Status.OK) {
      toastSuccess({ content: t('exalus.deletedSuccessfully') });
      onSuccess(false);
      navigate(-1);
    } else {
      handleError(result);
    }

    turnOffBackdrop();
  };

  const createOrEditUser = async (values: CreateEditUserForm, image?: string) => {
    turnOnBackdrop();

    const newUser = new User();
    newUser.Name = values.Name;
    newUser.Surname = values.Surname;
    newUser.Email = values.Email;
    newUser.Gender = values.Gender;
    newUser.AccessLevel = values.AccessLevel;
    if (values.Phone) newUser.Phone = values.Phone;
    if (userId) newUser.Guid = userId;

    const result = !userId
      ? await usersApi.CreateUserAsync(newUser, values.Password)
      : await usersApi.UpdateUserAsync(newUser);

    if (result.Type === Status.OK) {
      if (image) {
        const resultImage = await usersApi.SetUserProfilePictureAsync(newUser, image);

        if (resultImage.Type === Status.OK) {
          toastSuccess({ content: t(`exalus.${userId ? 'edited' : 'created'}Successfully`) });
          navigate(-1);
        } else {
          handleError(resultImage.Type, resultImage.Type === Status.WrongData ? t(`exalus.${result.Data}`) : '');
        }
      } else {
        toastSuccess({ content: t(`exalus.${userId ? 'edited' : 'created'}Successfully`) });
        navigate(-1);
      }
    } else {
      handleError(result.Type, result.Type === Status.WrongData ? t(`exalus.${result.Data}`) : '');
    }

    turnOffBackdrop();
  };

  return {
    changePassword,
    deleteUser,
    createOrEditUser,
  };
};
